// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2016
//
// ## Identify a single note on a staff
//
// Include description of object & useful info here, please!
import type { OneNoteSolfegeIDOptions } from './oneNoteSolfegeID';
import OneNoteSolfegeID from './oneNoteSolfegeID';
import type { InteractiveSettings } from './interactivePrototype';
import type { SolfegeQuestionObject } from '../uTheory.Skills/solfegeQuestionGenerator';
import SolfegeQuestionGenerator from '../uTheory.Skills/solfegeQuestionGenerator';
import Clef from '../uTheory.Notation/clef';
import Staff from '../uTheory.Notation/staff';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Editable from '../uTheory.Notation/editable';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';
import type Note from '../uTheory.Notation/note';
import { OneNoteSolfegeDictationInteractivePrototype } from './sharedSolfegeIdMethods';

export interface OneNoteSolfegeStaffIDOptions extends OneNoteSolfegeIDOptions {
  clefs?: string[];
}

export default class OneNoteSolfegeStaffID extends OneNoteSolfegeDictationInteractivePrototype {
  declare settings: InteractiveSettings;

  declare options: OneNoteSolfegeStaffIDOptions;

  noteHolder: NoteHolder;

  clef: Clef;

  generator = new SolfegeQuestionGenerator();

  targetSpeedFromSkills = OneNoteSolfegeID.registry.skills;

  protected staves: {
    staff: Staff;
    voice: Voice;
    noteHolder: NoteHolder;
    clef: Clef;
  }[];

  constructor(
    displayDiv: HTMLDivElement,
    options: OneNoteSolfegeStaffIDOptions = {},
    userData?: any
  ) {
    if (options.testMode) {
      throw new Error('One note solfege staff ID is not configured to run in checkpoint mode.');
    }
    super(displayDiv, options, userData);
    this.setSolfegeSystem();
    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: OneNoteSolfegeStaffIDOptions, userData?: any) {
    options.maxQuestions = options.maxQuestions || 24;
    this.generator = new SolfegeQuestionGenerator();

    if (options.levelFromUserData) options.level = this.getLevel(userData, 'single');
    else if (options.skills && !options.hasOwnProperty('level')) {
      options.level = this.setLevelFromSkill(options);
    } else if (options.hasOwnProperty('level') && !options.skills) {
      options.skills = [this.getSkillFromLevel(options)];
    } else options.level = this.getLevel(userData, 'single');

    this.settings = {
      loadAudioFirst: true,
      instructions: true,
      instructionsPanel: {
        innerHTML:
          '<p>You will hear a chord progression to establish the key,' +
          ' followed by a note in that key.</p>' +
          '<p>Draw the note on the staff.</p>',
        callback: this.startGame,
        thisArg: this,
      },
      editor: {
        fixedWidth: 220,
        ariaTitle: {
          rhythm: false,
          barline: false,
          selected: false,
          keySignature: { accidentalsCount: true, accidentalsNames: false },
        },
        ariaLive: 'assertive',
        disableTextInput: true,
      },
      letters: {
        checkButton: true,
        trashButton: true,
        accidentalButtons: options.level >= 5 ? { noDoubleTap: true } : undefined,
        customButtons: [
          { tagName: 'br' },
          {
            text: 'Play T<u>o</u>nic',
            value: 'playTonic',
            hotKey: ['o', 'O'],
            variable: 'playTonic',
          },
          { tagName: 'br', perspective: ['tall', 'portrait', 'square'] },
          { text: '<u>P</u>lay Note', value: 'play', hotKey: ['p', 'P'], variable: 'playNote' },
          { tagName: 'br' },
        ],
      },
      checkMark: {},
      player: {},
      progressTracker: true,
    };

    return super.init(displayDiv, options, userData);
  }

  generateQuestions(questions?: string[]): SolfegeQuestionObject[] {
    const solfegeQuestions = super.generateQuestions(questions, true);
    this.questionPrompt = 'Write the note you hear.';
    if (!this.optionsTrue('testMode')) {
      this.questionPrompt += `<br /><small>#{question} of ${this.questions.length}`;
    }
    return solfegeQuestions;
  }

  protected makeStaves() {
    if (this.options.level >= 9) {
      return ['treble', 'bass'].map(makeStaff, this);
    }

    if (this.optionSet('clefs')) {
      return this.options.clefs.map(makeStaff, this);
    }

    // In case someone forgets to set clef, assume treble
    return ['treble'].map(makeStaff, this);
  }

  initializeMusic() {
    const keySig = new KeySignature('C', 'Major');
    this.staves = this.makeStaves();

    const musicElements = this.staves.reduce(
      (arr, staff) => arr.concat([keySig, staff.staff, staff.voice]),
      []
    );
    this.music = new Music(musicElements);
  }

  addListeners() {
    function buttonHandler(input) {
      if (input === 'play') {
        this.play();
      } else if (input === 'playTonic') {
        this.playTonic();
      } else {
        const deleted = this.editor.delete();
        if (!deleted) {
          this.staves.forEach((s) => {
            s.noteHolder.length = 0;
          });
          this.editor.renderer.render();
        }
      }
    }

    this.letterPanel.addCustomButtonCallback(buttonHandler, this);
    this.letterPanel.addAccidentalCallback(this.editor.setAccidental, this.editor);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  startGame() {
    this.gameStarted = true;
    this.displayInstructions();
    this.resize();
  }

  /**
   * Clear the noteHolder, re-render the notation
   * Set the key signature for the key of the music being played and generate new question
   */
  newQuestion(disableAudio?: any) {
    this.staves.forEach((staff) => {
      staff.noteHolder.length = 0;
      staff.noteHolder.editable.selected = false;
    });

    super.newQuestion(disableAudio);
    if (this.currentQuestion.key) {
      this.staves.forEach((staff) => {
        staff.noteHolder.editable.setKeySignature(this.currentQuestion.key);
      });
      this.music.setKeySignature(this.currentQuestion.key);

      const clefs = this.currentQuestion.key.scale.prefersClef();
      const clef = clefs[~~(Math.random() * clefs.length)];
      if (this.staves.length < 2) {
        this.staves[0].noteHolder.editable.setClef(clef);
        this.music.staves[0].setClef(clef);
      } else {
        // this.editor.renderer.options.fixedWidth = 400;
        this.staves[0].noteHolder.editable.link(this.staves[1].noteHolder.editable, {
          maxNotes: 1,
          restIfEmptyAndLinkedFull: false,
        });
      }

      const accidentalOffset =
        this.currentQuestion.key.quality === 'minor' && this.levelData.level > 4 ? 15 : 0;
      const width =
        10 * Math.abs(this.currentQuestion.key.accidentalsCount) + 115 + accidentalOffset;
      this.editor.options.fixedWidth = width;
      this.editor.renderer.options.fixedWidth = width;
    }
    this.editor.renderer.render();
  }

  checkAnswer() {
    // If note is ghosting, nothing happens
    const isEmpty = this.staves.every((staff) => staff.noteHolder.length === 0);

    if (!this.options.testMode && isEmpty) {
      this.checkMark.warn('No note has been entered');
      return;
    }

    if (this.rightAnswerDelay) return;

    const noteArray = this.staves.reduce((arr, staff) => arr.concat(staff.noteHolder.notes), []);
    const answerNote = noteArray[0];
    const correctNote = this.currentQuestion.note;
    const correctIgnoreOctave = answerNote ? correctNote.equalsIgnoreOctave(answerNote) : false;
    const octaveOff = correctIgnoreOctave && correctNote.octave - answerNote.octave;

    // Yes, even in test mode...
    if (octaveOff) {
      if (octaveOff !== 0) {
        this.checkMark.warn('Wrong octave');
      }
      return;
    }

    // Test mode
    if (this.options.testMode) {
      this.checkpointAnswer({
        correctIgnoreOctave,
        answerNote,
      });
      return;
    }

    // Non-test mode
    if (correctIgnoreOctave) {
      this.rightAnswer(350);
      return;
    }

    const secondTry = this.wrongAnswerCount > 0;
    // Check and warn on first question of key if the user is mistakening the relative modes
    if (secondTry && this.currentQuestion.key) {
      const relativeKey = this.currentQuestion.key.getRelativeKey();
      const relativeNote = relativeKey.getNoteAtScaleDegreeOrThrow(this.currentQuestion.degree);
      if (relativeNote.letter === answerNote.letter) {
        this.wrongAnswer(
          `This is a ${this.currentQuestion.key.quality} key, not a ${relativeKey.quality} key`
        );
        return;
      }
    }

    const letterCorrect = correctNote.letter === answerNote.letter;
    // If note letter is correct but wrong accidental
    if (secondTry && letterCorrect) {
      this.wrongAnswer('Wrong accidental');
      return;
    }

    // If note letter is wrong and on 2nd or more try
    if (secondTry) {
      const noteLow = answerNote.isLessThan(correctNote);
      this.wrongAnswer(`Too ${noteLow ? 'low' : 'high'}`);
      return;
    }

    // Wrong on first try that is not off by octave
    this.wrongAnswer();
  }

  getAnswerAndData(answerObj: { answerNote?: Note; correct: boolean } = { correct: false }) {
    const correct = answerObj.correct;
    const studentAnswer = answerObj.answerNote;
    const question = this.currentQuestion.note.getName();

    const answerAndData = {
      question,
      questionType: 'OneNoteSolfegeStaffID',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: studentAnswer ? studentAnswer.getName() : '__',
      studentAnswerSVG: this.editor.renderer.exportSVG(),
      correctAnswerText: question,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: [this.currentQuestion.skills],
    };
    return answerAndData;
  }

  static registry = {
    description: 'One-note dictation',
    options: {
      levelFromUserData: true,
      tonicizeEvery: 6,
    },
    skills: [
      'sd_123_major',
      'sd_123_minor',
      'sd_12345_major',
      'sd_12345_minor',
      'sd_5678_major',
      'sd_5678_minor',
      'sd_5678_all_minors',
      'sd_1_to_8_major',
      'sd_1_to_8_minor',
      'sd_1_to_8_major_w_octaves',
      'sd_1_to_8_minor_w_octaves',
    ],
  };
}

function makeStaff(clefName) {
  const clef = new Clef(clefName);
  const staff = new Staff(clef);
  const nh = new NoteHolder('', 'w');
  nh.editable = new Editable(nh, {
    pitch: true,
    accidental: false,
    deleteable: ['ghost'],
    selectable: true,
  });
  const measure = new Measure(nh);
  const voice = new Voice(measure);
  voice.setStaff(staff);
  return {
    staff,
    voice,
    clef,
    noteHolder: nh,
  };
}
